/**
 * Projects Page
 *
 * CRUD interface for managing projects and versions under a selected matter.
 */

import React, { useState } from 'react';
import { Card, Stack, Flex, Button, Input, Modal, useTheme, spacing, textStyles, fontSize } from '@fti/design-system';
import { useProjects, useProjectMutations } from '../../api/projects';
import { useMatters } from '../../api/matters';
import { useClients } from '../../api/clients';
import { useHierarchy } from '../../context/HierarchyContext';
import { LoadingState } from '../shared/LoadingState';
import { ErrorDisplay } from '../shared/ErrorDisplay';
import { ConfirmModal } from '../shared/ConfirmModal';
import type { Project } from '../../api/types';
import { RoleGate } from '../shared/RoleGate';
import { GATES } from '../../auth/roles';
import { errorMessage } from '../../api/errors';

function ProjectFormModal({ isOpen, onClose, onSave, initialName, initialVersion, initialType, initialStartDate, initialEndDate, title }: {
  isOpen: boolean;
  onClose: () => void;
  onSave: (
    name: string,
    version: number,
    projecttype: 'static' | 'continuous',
    startDate: string,
    endDate: string
  ) => Promise<void>;
  initialName: string;
  initialVersion: string;
  initialType: 'static' | 'continuous';
  initialStartDate: string;
  initialEndDate: string;
  title: string;
}) {
  const { theme } = useTheme();
  const [name, setName] = useState(initialName);
  const [version, setVersion] = useState(initialVersion);
  const [projectType, setProjectType] = useState(initialType);
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const borderColor = theme.colors.border.default;

  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const inputStyle: React.CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: '4px',
    border: `1px solid ${borderColor}`,
    background: theme.colors.background.paper,
    color: textColor,
    fontSize: fontSize.sm,
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setErrorMsg('');
    try {
      await onSave(name.trim(), Number(version) || 1, projectType, startDate, endDate);
    } catch (err: any) {
      setErrorMsg(err?.message || 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <Modal.Header><h3 style={{ margin: 0, ...textStyles.label, color: textColor }}>{title}</h3></Modal.Header>
      <Modal.Body>
        <Stack gap="sm">
          <div>
            <label style={{ fontSize: fontSize.xs, color: mutedColor, display: 'block', marginBottom: '4px' }}>Project Name</label>
            <input placeholder="Project name" value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: fontSize.xs, color: mutedColor, display: 'block', marginBottom: '4px' }}>Version</label>
            <input type="number" value={version} onChange={(e) => setVersion(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: fontSize.xs, color: mutedColor, display: 'block', marginBottom: '4px' }}>Project Type</label>
            <select
              value={projectType}
              onChange={(e) => setProjectType(e.target.value as 'static' | 'continuous')}
              style={inputStyle}
            >
              <option value="static">Static</option>
              <option value="continuous">Continuous</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: fontSize.xs, color: mutedColor, display: 'block', marginBottom: '4px' }}>Data Start Date</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: fontSize.xs, color: mutedColor, display: 'block', marginBottom: '4px' }}>Data End Date <span style={{ color: mutedColor }}>(blank = ongoing)</span></label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={inputStyle} />
          </div>
        </Stack>
          {errorMsg && <span style={{ color: theme.colors.semantic.error.default, fontSize: fontSize.xs }}>{errorMsg}</span>}
      </Modal.Body>
      <Modal.Footer>
        <Flex gap="sm" justify="end">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="solid" color="primary" size="sm" onClick={handleSave} disabled={!name.trim() || saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Flex>
      </Modal.Footer>
    </Modal>
  );
}

export function ProjectsPage() {
  const { theme } = useTheme();
  const { selectedClient, selectedMatter, selectProjectName, selectProject, bumpData } = useHierarchy();
  const [showAll, setShowAll] = useState(false);

  // When "show all" is on (or no matter is selected), fetch every project;
  // otherwise scope to the selected matter.
  const scopeToMatter = !!selectedMatter && !showAll;
  const { data: projects, loading, error, refetch } = useProjects(
    scopeToMatter ? { underMatterId: selectedMatter.id, limit: 1000 } : { limit: 1000 }
  );
  const { createProject, updateProject, deleteProject } = useProjectMutations();

  // Matter/client code lookups for the "show all" view (project -> matter -> client).
  const { data: allMatters } = useMatters({ limit: 1000 });
  const { data: clients } = useClients({ limit: 1000 });
  const matterInfo = React.useCallback(
    (idMatter: number) => {
      const matter = allMatters?.find((m) => m.id === idMatter);
      const client = matter ? clients?.find((c) => c.id === matter.id_client) : undefined;
      return {
        mattercode: matter?.mattercode || '—',
        clientcode: client?.clientcode || '—',
      };
    },
    [allMatters, clients]
  );

  // In "show all", surface projects under the selected matter first.
  const orderedProjects = React.useMemo(() => {
    if (!projects) return projects;
    if (!showAll || !selectedMatter) return projects;
    const inCtx = projects.filter((p) => p.id_matter === selectedMatter.id);
    const rest = projects.filter((p) => p.id_matter !== selectedMatter.id);
    return [...inCtx, ...rest];
  }, [projects, showAll, selectedMatter]);

  const [search, setSearch] = useState('');
  const filteredProjects = React.useMemo(() => {
    if (!orderedProjects) return orderedProjects;
    const q = search.trim().toLowerCase();
    if (!q) return orderedProjects;
    return orderedProjects.filter((p) => {
      const { mattercode, clientcode } = matterInfo(p.id_matter);
      return (
        p.name.toLowerCase().includes(q) ||
        String(p.version).toLowerCase().includes(q) ||
        mattercode.toLowerCase().includes(q) ||
        clientcode.toLowerCase().includes(q)
      );
    });
  }, [orderedProjects, search, matterInfo]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Project | null>(null);
  const [deleteBlockedMsg, setDeleteBlockedMsg] = useState('');

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const borderColor = theme.colors.border.default;

  const handleSaveNew = async (
    name: string,
    version: number,
    projecttype: 'static' | 'continuous',
    startDate: string,
    endDate: string
  ) => {
    if (!selectedMatter) return;
    const created = await createProject({
      name,
      id_matter: selectedMatter.id,
      version,
      projecttype,
      start_ts: startDate ? `${startDate}T00:00:00` : undefined,
      end_ts: endDate ? `${endDate}T00:00:00` : undefined,
    });
    setShowAddModal(false);
    // Select the new project version into context immediately (set the project
    // name for the version cascade, then the version itself).
    if (created) {
      selectProjectName(created.name);
      selectProject(created);
    }
    refetch();
    bumpData();
  };

  const handleSaveEdit = async (
    name: string,
    version: number,
    projecttype: 'static' | 'continuous',
    startDate: string,
    endDate: string
  ) => {
    if (!editingProject) return;
    await updateProject(editingProject.id, {
      name,
      version,
      projecttype,
      start_ts: startDate ? `${startDate}T00:00:00` : undefined,
      end_ts: endDate ? `${endDate}T00:00:00` : undefined,
    });
    setEditingProject(null);
    refetch();
    bumpData();
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteProject(deleteTarget.id);
      setDeleteTarget(null);
      refetch();
    bumpData();
    } catch (err: unknown) {
      // The service explains its own refusal — naming how many children block the
      // delete — so show that rather than a guess. The hardcoded text below is a
      // fallback only: it happens to be right for the common case, which is exactly
      // what makes substituting it dangerous. A 409 raised for any other reason
      // would be reported as a child-record problem the user cannot find.
      setDeleteBlockedMsg(
        errorMessage(err, 'Cannot delete: this project has associated models or vectors. Remove them first.')
      );
    }
  };

  if (!selectedMatter && !showAll) {
    return (
      <Stack gap="lg">
        <h1 style={{ ...textStyles.h3, color: textColor }}>Projects</h1>
        <Card variant="outlined">
          <Card.Body>
            <Flex direction="column" align="center" gap="sm" style={{ padding: spacing.lg }}>
              <span style={{ color: mutedColor, fontSize: fontSize.sm }}>
                Select a client and matter from the context bar to view projects.
              </span>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', color: mutedColor, fontSize: fontSize.sm, cursor: 'pointer' }}>
                <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
                Show all projects
              </label>
            </Flex>
          </Card.Body>
        </Card>
      </Stack>
    );
  }

  if (loading) return <LoadingState message="Loading projects..." />;
  if (error) return <ErrorDisplay error={error} onRetry={refetch} />;

  return (
    <Stack gap="lg">
      <Flex justify="between" align="center">
        <div>
          <h1 style={{ ...textStyles.h3, color: textColor }}>Projects</h1>
          <p style={{ margin: 0, marginTop: spacing.xs, fontSize: fontSize.sm, color: mutedColor }}>
            {showAll ? 'All projects' : `Under: ${selectedClient?.name} / ${selectedMatter?.name}`}
          </p>
        </div>
        <Flex gap="md" align="center">
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', color: mutedColor, fontSize: fontSize.sm, cursor: 'pointer' }}>
            <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
            Show all
          </label>
          <RoleGate minRole={GATES.hierarchyWrite}>
            <Button variant="solid" color="primary" size="sm" disabled={!selectedMatter} onClick={() => setShowAddModal(true)}>+ Add Project</Button>
          </RoleGate>
        </Flex>
      </Flex>

      <Input
        placeholder="Search projects by name, version, or code..."
        value={search}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
      />

      <Card variant="elevated">
        <Card.Body>
          {filteredProjects && filteredProjects.length > 0 ? (
            <div style={{ border: `1px solid ${borderColor}`, borderRadius: '8px', overflow: 'hidden' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: showAll ? '1fr 90px 110px 130px 80px 100px 100px 100px' : '1fr 90px 80px 100px 100px 100px',
                padding: `${spacing.sm} ${spacing.md}`,
                background: theme.colors.background.paper,
                borderBottom: `1px solid ${borderColor}`,
                fontSize: fontSize.xs,
                fontWeight: 600,
                color: mutedColor,
                textTransform: 'uppercase' as const,
              }}>
                <span>Name</span>
                <span>id_project</span>
                {showAll && <span>Client Code</span>}
                {showAll && <span>Matter Code</span>}
                <span>Version</span>
                <span>Type</span>
                <span>Status</span>
                <span style={{ textAlign: 'right' }}>Actions</span>
              </div>
              {filteredProjects.map((project) => (
                <div
                  key={project.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: showAll ? '1fr 90px 110px 130px 80px 100px 100px 100px' : '1fr 90px 80px 100px 100px 100px',
                    padding: `${spacing.sm} ${spacing.md}`,
                    borderBottom: `1px solid ${borderColor}`,
                    alignItems: 'center',
                    fontSize: fontSize.sm,
                    cursor: 'pointer',
                  }}
                  onClick={() => selectProject(project)}
                >
                  <span style={{ fontWeight: 500, color: textColor }}>{project.name}</span>
                  <span style={{ color: mutedColor, fontFamily: 'monospace', fontSize: fontSize.xs }}>
                    {project.id}
                  </span>
                  {showAll && (
                    <span style={{ color: mutedColor, fontFamily: 'monospace', fontSize: fontSize.xs }}>
                      {matterInfo(project.id_matter).clientcode}
                    </span>
                  )}
                  {showAll && (
                    <span style={{ color: mutedColor, fontFamily: 'monospace', fontSize: fontSize.xs }}>
                      {matterInfo(project.id_matter).mattercode}
                    </span>
                  )}
                  <span style={{ color: mutedColor }}>v{project.version}</span>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: `2px ${spacing.xs}`,
                    borderRadius: '4px',
                    background: project.projecttype === 'continuous' ? `${theme.colors.primary[600]}15` : `${theme.colors.neutral[300]}20`,
                    color: project.projecttype === 'continuous' ? theme.colors.primary[600] : mutedColor,
                    fontSize: fontSize.xs,
                    fontWeight: 600,
                    whiteSpace: 'nowrap' as const,
                  }}>
                    {project.projecttype}
                  </span>
                  <span style={{ color: mutedColor, fontSize: fontSize.xs }}>{project.status || '—'}</span>
                  <Flex gap="xs" justify="end" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                    <RoleGate minRole={GATES.hierarchyWrite}>
                      <Button variant="ghost" size="sm" onClick={() => setEditingProject(project)}>Edit</Button>
                      <Button variant="ghost" size="sm" color="error" onClick={() => { setDeleteTarget(project); setDeleteBlockedMsg(''); }}>Delete</Button>
                    </RoleGate>
                  </Flex>
                </div>
              ))}
            </div>
          ) : (
            <Flex direction="column" align="center" style={{ padding: spacing.lg }}>
              <span style={{ color: mutedColor, fontSize: fontSize.sm }}>{search ? 'No projects match your search' : showAll ? 'No projects found' : 'No projects found for this matter'}</span>
            </Flex>
          )}
        </Card.Body>
      </Card>

      {/* Add Modal */}
      {showAddModal && (
        <ProjectFormModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSave={handleSaveNew}
          initialName=""
          initialVersion="1"
          initialType="static"
          initialStartDate=""
          initialEndDate=""
          title="Add Project"
        />
      )}

      {/* Edit Modal */}
      {editingProject && (
        <ProjectFormModal
          isOpen={!!editingProject}
          onClose={() => setEditingProject(null)}
          onSave={handleSaveEdit}
          initialName={editingProject.name}
          initialVersion={String(editingProject.version)}
          initialType={editingProject.projecttype}
          initialStartDate={editingProject.start_ts ? String(editingProject.start_ts).slice(0, 10) : ''}
          initialEndDate={editingProject.end_ts ? String(editingProject.end_ts).slice(0, 10) : ''}
          title="Edit Project"
        />
      )}

      <ConfirmModal
        isOpen={!!deleteTarget}
        title="Delete Project"
        message={deleteBlockedMsg || `Are you sure you want to delete "${deleteTarget?.name}"?`}
        confirmLabel={deleteBlockedMsg ? 'OK' : 'Delete'}
        variant={deleteBlockedMsg ? 'warning' : 'danger'}
        onConfirm={deleteBlockedMsg ? () => setDeleteTarget(null) : handleDeleteConfirm}
        onCancel={() => { setDeleteTarget(null); setDeleteBlockedMsg(''); }}
      />
    </Stack>
  );
}
